import { GitHubService } from '../services/GitHubService';
import { androidSafeObjectUrl } from '../utils/androidSafeBlobUrl';
import { mimeForMediaPlaybackPath } from '../utils/itemMediaPlayback';
import { alternateVideoRepoPaths } from '../utils/itemVideo';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import {
  authenticatedVideoStreamUrlCandidates,
  githubAuthenticatedRawUrl,
  repoIsPrivate,
  videoStreamUrlCandidates,
} from '../utils/githubMediaUrl';
import { githubMediaProxyUrl } from '../utils/githubMediaProxyUrl';
import {
  mp4HasFastStart,
  optimisticPrivateStreamProbe,
  probeMediaBytes,
} from '../utils/mediaBytesProbe';
import { isMediaDebugEnabled, logMediaDebug } from '../utils/mediaDebugLog';
import { mp4FastStartBytes } from '../utils/mp4FastStart';
import {
  prefersVideoBlobOverProxy,
  prefersVideoHttpsStreamFirst,
  prefersVideoStreamPlayback,
  type VideoPlaybackOptions,
} from '../utils/videoPlaybackMode';

/** Blob / stream URL cache for item video clips in the file view. */
export class AppVideoCache {
  private readonly urls = new Map<string, string>();

  private releaseUrl(url: string): void {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }

  revokeAll(): void {
    for (const url of this.urls.values()) this.releaseUrl(url);
    this.urls.clear();
  }

  private async probeProxyFastStart(
    github: GitHubService,
    path: string
  ): Promise<boolean> {
    try {
      const url = githubMediaProxyUrl(github.getConfig(), path);
      const res = await fetch(url, {
        headers: { Range: 'bytes=0-2097151' },
        cache: 'no-store',
      });
      if (!res.ok && res.status !== 206) return false;
      const bytes = new Uint8Array(await res.arrayBuffer());
      return mp4HasFastStart(bytes) === true;
    } catch {
      return false;
    }
  }

  private async probeStreamHead(
    streamUrl: string,
    repoPath: string
  ): Promise<ReturnType<typeof probeMediaBytes> | undefined> {
    try {
      const res = await fetch(streamUrl, {
        headers: { Range: 'bytes=0-524287' },
        cache: 'no-store',
      });
      if (!res.ok && res.status !== 206) return undefined;
      const bytes = new Uint8Array(await res.arrayBuffer());
      return probeMediaBytes(bytes, repoPath);
    } catch {
      return undefined;
    }
  }

  private async tryStreamUrl(
    github: GitHubService,
    path: string,
    cacheKey: string,
    opts?: VideoPlaybackOptions
  ): Promise<string | null> {
    const config = github.getConfig();
    const isPrivate = repoIsPrivate(config);
    const firefox = prefersVideoBlobOverProxy();
    const candidates = opts?.rawStreamOnly
      ? [githubAuthenticatedRawUrl(config, path)]
      : isPrivate
        ? authenticatedVideoStreamUrlCandidates(config, path, firefox)
        : videoStreamUrlCandidates(config, path, firefox);
    const apiUrl = await withoutGitBusyOverlay(() => github.getMediaStreamUrl(path));
    if (apiUrl && !candidates.includes(apiUrl)) candidates.push(apiUrl);

    for (const streamUrl of candidates) {
      const isPatRaw =
        streamUrl.includes('raw.githubusercontent.com') && streamUrl.includes('token=');
      const skipCorsProbe = isPatRaw && isPrivate && config.pat.trim().length > 0;
      const probe = skipCorsProbe
        ? optimisticPrivateStreamProbe(path)
        : await this.probeStreamHead(streamUrl, path);
      if (!probe || probe.issue) {
        if (isMediaDebugEnabled()) {
          logMediaDebug({
            kind: 'video',
            phase: 'stream-skip',
            repoPath: path,
            ok: false,
            message: probe?.issue ?? 'stream probe failed (CORS or HTTP error)',
            ...(probe ? { probe } : {}),
            details: { streamUrl: streamUrl.slice(0, 80), skipCorsProbe },
          });
        }
        continue;
      }

      if (isMediaDebugEnabled()) {
        const viaProxy = streamUrl.startsWith('/api/');
        const viaPatRaw = streamUrl.includes('raw.githubusercontent.com') && streamUrl.includes('token=');
        logMediaDebug({
          kind: 'video',
          phase: viaProxy ? 'proxy-url' : 'stream-url',
          repoPath: path,
          ok: true,
          message: isPrivate
            ? viaProxy
              ? 'Using same-origin proxy (PAT in query)'
              : viaPatRaw
                ? 'Using tokenized raw.githubusercontent.com (PAT from settings)'
                : 'Using Contents API download_url'
            : 'Using HTTPS CDN stream (Range playback)',
          probe,
          details: { streamUrl: streamUrl.slice(0, 80), cacheKey, isPrivate },
        });
      }

      this.urls.set(cacheKey, streamUrl);
      return streamUrl;
    }

    return null;
  }

  private async tryProxyUrl(
    github: GitHubService,
    path: string,
    cacheKey: string
  ): Promise<string | null> {
    const fastOnServer = await this.probeProxyFastStart(github, path);
    if (!fastOnServer) return null;

    const proxyUrl = githubMediaProxyUrl(github.getConfig(), path);
    if (isMediaDebugEnabled()) {
      logMediaDebug({
        kind: 'video',
        phase: 'proxy-url',
        repoPath: path,
        ok: true,
        message: 'Using same-origin proxy (fast-start on server)',
        details: { proxyUrl: proxyUrl.slice(0, 80), cacheKey },
      });
    }
    this.urls.set(cacheKey, proxyUrl);
    return proxyUrl;
  }

  private async tryBlobUrl(
    github: GitHubService,
    path: string,
    cacheKey: string
  ): Promise<string> {
    let bytes = (await withoutGitBusyOverlay(() => github.readBinary(path))).bytes;
    let probe = probeMediaBytes(bytes, path);
    const remuxForFirefox = prefersVideoBlobOverProxy();
    if (remuxForFirefox || probe.mp4FastStart === false) {
      const remuxed = mp4FastStartBytes(bytes);
      if (remuxed) {
        bytes = remuxed;
        probe = probeMediaBytes(bytes, path);
        if (isMediaDebugEnabled()) {
          logMediaDebug({
            kind: 'video',
            phase: 'faststart-remux',
            repoPath: path,
            ok: true,
            message: remuxForFirefox
              ? 'Remuxed MP4 for Firefox playback'
              : 'Remuxed MP4 for fast start (moov before mdat)',
            probe,
          });
        }
      }
    }
    const mime = mimeForMediaPlaybackPath(path, bytes);
    if (isMediaDebugEnabled()) {
      logMediaDebug({
        kind: 'video',
        phase: 'fetch-ok',
        repoPath: path,
        ok: !probe.issue,
        message:
          probe.issue ??
          probe.warning ??
          `Loaded ${probe.byteLength} bytes as ${mime}`,
        probe,
        details: { mime, cacheKey },
      });
    }
    if (probe.issue) throw new Error(probe.issue);

    const filename = path.split('/').pop() ?? 'clip.mp4';
    const url = await androidSafeObjectUrl(bytes, mime, filename);
    this.urls.set(cacheKey, url);
    if (isMediaDebugEnabled()) {
      logMediaDebug({
        kind: 'video',
        phase: 'blob-url',
        repoPath: path,
        ok: true,
        message: prefersVideoStreamPlayback()
          ? `Mobile-safe blob URL (${mime})`
          : `Created blob URL (${mime})`,
        probe,
        details: { blobBytes: bytes.byteLength },
      });
    }
    return url;
  }

  async getPlaybackUrl(
    github: GitHubService,
    repoPath: string,
    bustCache = false,
    mdPath?: string,
    opts?: VideoPlaybackOptions
  ): Promise<string> {
    if (opts?.openInNewTab) {
      const pathsToTry = alternateVideoRepoPaths(repoPath, mdPath);
      const path = pathsToTry[0] ?? repoPath;
      return githubMediaProxyUrl(github.getConfig(), path);
    }

    const cacheKey = `video:v4:${mdPath ?? ''}:${repoPath}`;
    if (bustCache) {
      const old = this.urls.get(cacheKey);
      if (old) this.releaseUrl(old);
      this.urls.delete(cacheKey);
    }
    const cached = this.urls.get(cacheKey);
    if (cached) {
      const staleProxy = opts?.skipProxy && cached.startsWith('/api/');
      const staleBlob = opts?.streamOnly && cached.startsWith('blob:');
      const staleRaw =
        opts?.rawStreamOnly &&
        !cached.includes('raw.githubusercontent.com') &&
        !cached.includes('token=');
      if (!staleProxy && !staleBlob && !staleRaw) return cached;
      this.releaseUrl(cached);
      this.urls.delete(cacheKey);
    }

    const pathsToTry = alternateVideoRepoPaths(repoPath, mdPath);
    const config = github.getConfig();
    const isPrivate = repoIsPrivate(config);
    const onMobile = prefersVideoStreamPlayback();
    const firefox = prefersVideoBlobOverProxy();
    const streamFirst = prefersVideoHttpsStreamFirst() && !isPrivate;
    const authenticatedStream = isPrivate && onMobile;
    const skipProxy = Boolean(opts?.skipProxy) || (firefox && !isPrivate);
    const streamOnly = Boolean(opts?.streamOnly);
    let lastErr: unknown;

    if (isMediaDebugEnabled()) {
      logMediaDebug({
        kind: 'video',
        phase: 'fetch-start',
        repoPath,
        ok: true,
        message: `Trying ${pathsToTry.length} path(s)${onMobile ? ' (mobile)' : ''}${isPrivate ? ' (private+PAT)' : ''}${streamOnly ? ' (stream)' : streamFirst || authenticatedStream ? ' (stream→blob)' : skipProxy ? ' (blob)' : ''}`,
        details: {
          paths: pathsToTry,
          mdPath: mdPath ?? null,
          onMobile,
          isPrivate,
          authenticatedStream,
          skipProxy,
          streamFirst,
          streamOnly,
          firefox,
        },
      });
    }

    for (const path of pathsToTry) {
      if (onMobile && !skipProxy && !authenticatedStream) {
        try {
          const proxy = await this.tryProxyUrl(github, path, cacheKey);
          if (proxy) return proxy;
        } catch (err) {
          lastErr = err;
          if (isMediaDebugEnabled()) {
            logMediaDebug({
              kind: 'video',
              phase: 'proxy-fail',
              repoPath: path,
              ok: false,
              message: err instanceof Error ? err.message : String(err),
            });
          }
        }
      }

      if ((streamFirst || streamOnly || authenticatedStream) && !opts?.skipStream) {
        try {
          const stream = await this.tryStreamUrl(github, path, cacheKey, opts);
          if (stream) return stream;
        } catch (err) {
          lastErr = err;
          if (isMediaDebugEnabled()) {
            logMediaDebug({
              kind: 'video',
              phase: 'stream-fail',
              repoPath: path,
              ok: false,
              message: err instanceof Error ? err.message : String(err),
            });
          }
        }
        if (streamOnly) continue;
      }

      try {
        return await this.tryBlobUrl(github, path, cacheKey);
      } catch (err) {
        lastErr = err;
        if (isMediaDebugEnabled()) {
          logMediaDebug({
            kind: 'video',
            phase: 'fetch-fail',
            repoPath: path,
            ok: false,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    }

    const message = lastErr instanceof Error ? lastErr.message : 'Could not load video';
    if (isMediaDebugEnabled()) {
      logMediaDebug({
        kind: 'video',
        phase: 'fetch-exhausted',
        repoPath,
        ok: false,
        message,
      });
    }
    throw lastErr instanceof Error ? lastErr : new Error('Could not load video');
  }
}
